import React, { useEffect, useState } from 'react';
import { Shield, Calendar, User, FileText, AlertCircle, Loader2, Download } from 'lucide-react';
import { AuditService } from '../services/auditService';
import type { AuditLog, AuditLogFilters } from '../types/audit';
import { format } from 'date-fns';

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters] = useState<AuditLogFilters>({});
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  const loadAuditLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await AuditService.getAuditLogs(filters, 50, 0);
      const { count } = await AuditService.getAuditLogCount(filters);
      setLogs(data);
      setTotalCount(count);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action: string) => {
    if (action.includes('create')) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
    if (action.includes('update')) return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20';
    if (action.includes('delete')) return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
    return 'text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20';
  };

  const handleExport = async () => {
    try {
      const { data } = await AuditService.getAuditLogs(filters, 1000, 0);
      const csv = [
        ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'IP Address', 'Details'].join(','),
        ...data.map(log => [
          log.created_at,
          log.user_email || log.user_id || 'System',
          log.action,
          log.resource_type || '',
          log.resource_id || '',
          log.ip_address || '',
          JSON.stringify(log.metadata || {})
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
    } catch (err) {
      setError('Failed to export audit logs');
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Shield size={20} className="text-primary-600" />
            Audit Logs
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Track all system activities and changes ({totalCount} total records)
          </p>
        </div>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Timestamp</th>
                <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400">User</th>
                <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Action</th>
                <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Entity</th>
                <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-slate-400" />
                      {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm')}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-slate-400" />
                      {log.user_email || (log.user_id ? log.user_id.substring(0, 8) : 'System')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-slate-400" />
                      {log.resource_type || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-500 dark:text-slate-400 text-xs max-w-xs truncate">
                    {log.metadata ? JSON.stringify(log.metadata) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;

